if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super()
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        dots: this.querySelector('.product-gallery-dots')
      }
      this.mql = window.matchMedia('(min-width: 750px)')
      this.mobileMql = window.matchMedia('(max-width: 879px)')
      this.thumbnailAnimating = false
      this.pendingThumbnail = null
      this.loopJumping = false
      this.loopScrollTimer = null

      requestAnimationFrame(() => this.initMainSliderLoop())

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this))
      }
      if (!this.elements.thumbnails) return

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false))
      })
      requestAnimationFrame(() => this.updateThumbnailLayout())
      this.thumbnailResizeObserver = new ResizeObserver(() => this.updateThumbnailLayout())
      this.thumbnailResizeObserver.observe(this.elements.thumbnails)
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic()
    }

    getRealSlides() {
      if (!this.elements.viewer) return []
      return Array.from(this.elements.viewer.querySelectorAll('.product__media-item:not(.is-clone)'))
    }

    getAllSlides() {
      if (!this.elements.viewer || !this.elements.viewer.slider) return []
      return Array.from(this.elements.viewer.slider.querySelectorAll('.product__media-item'))
    }

    getVisibleSlide() {
      const slider = this.elements.viewer && this.elements.viewer.slider
      const slides = this.getAllSlides()
      if (!slider || slides.length < 2) return slides[0] || null
      const offset = slides[1].offsetLeft - slides[0].offsetLeft
      if (!offset) return slides[0]
      const index = Math.round(slider.scrollLeft / offset)
      return slides[Math.max(0, Math.min(index, slides.length - 1))]
    }

    initMainSliderLoop() {
      if (!this.mobileMql.matches || this.loopEnabled) return
      const slider = this.elements.viewer && this.elements.viewer.slider
      const slides = this.getRealSlides()
      if (!slider || slides.length < 2) return

      const first = slides[0]
      const last = slides[slides.length - 1]
      slider.insertBefore(this.createLoopClone(last), first)
      slider.appendChild(this.createLoopClone(first))

      if (this.elements.viewer.resetPages) this.elements.viewer.resetPages()
      this.jumpToSlide(first)
      this.loopEnabled = true

      const onScrollSettled = () => {
        if (this.loopJumping) return
        this.snapLoopPosition()
      }
      slider.addEventListener('scrollend', onScrollSettled)
      slider.addEventListener('scroll', () => {
        if (this.loopJumping) return
        clearTimeout(this.loopScrollTimer)
        this.loopScrollTimer = setTimeout(onScrollSettled, 140)
      }, { passive: true })
    }

    createLoopClone(slide) {
      const clone = slide.cloneNode(true)
      clone.classList.add('is-clone')
      clone.classList.remove('is-active')
      clone.removeAttribute('id')
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'))
      clone.querySelectorAll('button, a').forEach((el) => el.setAttribute('tabindex', '-1'))
      return clone
    }

    snapLoopPosition() {
      const slider = this.elements.viewer && this.elements.viewer.slider
      const slides = this.getAllSlides()
      if (!slider || slides.length < 3 || this.loopJumping) return

      const firstClone = slides[0]
      const lastClone = slides[slides.length - 1]
      const threshold = 8

      if (Math.abs(slider.scrollLeft - lastClone.offsetLeft) <= threshold) {
        this.jumpToSlide(slides[1])
        return
      }
      if (Math.abs(slider.scrollLeft - firstClone.offsetLeft) <= threshold) {
        this.jumpToSlide(slides[slides.length - 2])
      }
    }

    jumpToSlide(slide) {
      const slider = this.elements.viewer && this.elements.viewer.slider
      if (!slider || !slide) return
      this.loopJumping = true
      const snap = slider.style.scrollSnapType
      slider.style.scrollSnapType = 'none'
      slider.style.scrollBehavior = 'auto'
      slider.scrollLeft = slide.offsetLeft
      slider.style.scrollSnapType = snap
      clearTimeout(this.loopJumpTimer)
      this.loopJumpTimer = setTimeout(() => {
        this.loopJumping = false
      }, 80)
    }

    onSlideChanged(event) {
      const current = this.getVisibleSlide() || event.detail.currentElement
      if (!current) return
      this.updateDots(current.dataset.mediaId)

      if (!this.elements.thumbnails) return
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`)
      this.setActiveThumbnail(thumbnail)
    }

    updateDots(mediaId) {
      if (!this.elements.dots) return
      const dots = this.elements.dots.querySelectorAll('.product-gallery-dots__item')
      this.getRealSlides().forEach((slide, index) => {
        if (!dots[index]) return
        dots[index].classList.toggle('is-active', slide.dataset.mediaId === mediaId)
      })
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not(.is-clone)`)
      this.elements.viewer.querySelectorAll('.product__media-item:not(.is-clone)').forEach((element) => {
        element.classList.remove('is-active')
      })
      activeMedia.classList.add('is-active')

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia)
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`)
          activeThumbnail.parentElement.prepend(activeThumbnail)
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages()
      }

      this.preventStickyHeader()
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft })
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' })
        }
      })
      this.playActiveMedia(activeMedia)
      this.updateDots(mediaId)

      if (!this.elements.thumbnails) return
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`)
      this.setActiveThumbnail(activeThumbnail)
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition)
    }

    getThumbnailList() {
      return this.elements.thumbnails && this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]')
    }

    getThumbnailMetrics() {
      const list = this.getThumbnailList()
      const slider = this.elements.thumbnails
      if (!list || !slider) return null
      const items = Array.from(list.children)
      if (!items.length) return null
      const styles = window.getComputedStyle(list)
      const gap = parseFloat(styles.columnGap || styles.gap) || 0
      const itemWidth = items[0].getBoundingClientRect().width
      const totalWidth = items.length * itemWidth + (items.length - 1) * gap
      return {
        list,
        slider,
        items,
        gap,
        itemWidth,
        totalWidth,
        containerWidth: slider.clientWidth
      }
    }

    isThumbnailOverflow() {
      const metrics = this.getThumbnailMetrics()
      if (!metrics) return false
      return metrics.totalWidth > metrics.containerWidth
    }

    resetThumbnailList(list) {
      this.thumbnailAnimating = false
      this.pendingThumbnail = null
      const items = Array.from(list.children)
      items.sort((a, b) => Number(a.dataset.mediaPosition) - Number(b.dataset.mediaPosition))
      items.forEach((item) => list.appendChild(item))
      list.style.transition = 'none'
      list.style.transform = 'translateX(0)'
      list.scrollLeft = 0
      list.offsetHeight
      list.style.transition = ''
    }

    updateThumbnailLayout() {
      if (!this.mobileMql.matches || !this.elements.thumbnails) return
      const metrics = this.getThumbnailMetrics()
      if (!metrics) return

      const overflow = metrics.totalWidth > metrics.containerWidth
      const wasOverflow = this.elements.thumbnails.classList.contains('thumbnail-slider--overflow')

      this.elements.thumbnails.classList.toggle('thumbnail-slider--overflow', overflow)
      this.elements.thumbnails.classList.toggle('thumbnail-slider--centered', !overflow)

      if (!overflow) {
        this.resetThumbnailList(metrics.list)
        return
      }

      if (!wasOverflow) {
        const active = metrics.list.querySelector('button[aria-current="true"]')?.closest('.slider__slide')
        if (active) this.alignActiveThumbnail(active)
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'))
      thumbnail.querySelector('button').setAttribute('aria-current', true)

      if (this.mobileMql.matches) {
        if (this.isThumbnailOverflow()) this.alignActiveThumbnail(thumbnail)
        return
      }

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return
      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft })
    }

    alignActiveThumbnail(thumbnail) {
      const list = this.getThumbnailList()
      if (!list || !thumbnail || !this.isThumbnailOverflow()) return

      if (this.thumbnailAnimating) {
        this.pendingThumbnail = thumbnail
        return
      }

      const items = Array.from(list.children)
      const index = items.indexOf(thumbnail)
      if (index <= 0) {
        list.scrollLeft = 0
        return
      }

      const styles = window.getComputedStyle(list)
      const gap = parseFloat(styles.columnGap || styles.gap) || 0
      const step = items[0].getBoundingClientRect().width + gap
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const goBackward = index > items.length / 2
      let settled = false

      const finish = () => {
        if (settled) return
        settled = true
        list.style.transition = 'none'
        list.style.transform = 'translateX(0)'
        list.scrollLeft = 0
        list.offsetHeight
        list.style.transition = ''
        this.thumbnailAnimating = false
        const pending = this.pendingThumbnail
        this.pendingThumbnail = null
        if (pending && pending !== thumbnail) this.alignActiveThumbnail(pending)
      }

      const afterTransition = (callback) => {
        let done = false
        const onEnd = (event) => {
          if (done) return
          if (event && event.target !== list) return
          if (event && event.propertyName && event.propertyName !== 'transform') return
          done = true
          list.removeEventListener('transitionend', onEnd)
          callback()
        }
        list.addEventListener('transitionend', onEnd)
        setTimeout(onEnd, 400)
      }

      this.thumbnailAnimating = true

      if (reduceMotion) {
        if (goBackward) {
          items.slice(index).reverse().forEach((item) => list.insertBefore(item, list.firstChild))
        } else {
          items.slice(0, index).forEach((item) => list.appendChild(item))
        }
        finish()
        return
      }

      if (goBackward) {
        const moving = items.slice(index)
        list.style.transition = 'none'
        moving.reverse().forEach((item) => list.insertBefore(item, list.firstChild))
        list.style.transform = `translateX(${-step * moving.length}px)`
        list.offsetHeight
        requestAnimationFrame(() => {
          list.style.transition = 'transform 0.35s ease'
          list.style.transform = 'translateX(0)'
        })
        afterTransition(finish)
        return
      }

      list.style.transition = 'transform 0.35s ease'
      list.style.transform = `translateX(${-step * index}px)`
      afterTransition(() => {
        items.slice(0, index).forEach((item) => list.appendChild(item))
        finish()
      })
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img')
      if (!image) return
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false)
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        )
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true)
        }, 2000)
      }
      image.src = image.src
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia()
      const deferredMedia = activeItem.querySelector('.deferred-media')
      if (deferredMedia) deferredMedia.loadContent(false)
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header')
      if (!this.stickyHeader) return
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return
      this.elements.viewer.slider.setAttribute('role', 'presentation')
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'))
    }
  })
}
